import {
  AUTH_SESSION_EXPIRED_EVENT,
  AuthEnum,
  getSessionExpiration,
} from "../context";

export const baseURL = process.env.REACT_APP_API_BASE_URL;

const url = (path: string, complementURL?: string) =>
  `${baseURL}/${path}${complementURL ? `${complementURL}` : ""}`;

function createEndpoints<T extends Record<string, string>>(defs: T) {
  const result: Record<keyof T, (complementURL?: string) => string> = {} as any;

  for (const key in defs) {
    const path = defs[key];
    result[key] = (complementURL?: string) => url(path, complementURL);
  }

  return result as { [K in keyof T]: (complementURL?: string) => string };
}

export const API_URL = createEndpoints({
  AUTH_SIGNIN: "login",
  AUTH_REFRESH: "token/refresh",
  CARDS: "cards",
  SALES_MY_PRODUCTS: "transactions",
  OWN_SALES: "ownsales",
  DASHBOARD: "dashboard",
  ADMIN_USERS: "admin/users",
  ADMIN_AREA01_PRODUCTS: "admin/area01-products",
  ADMIN_UNLINKED_EANS: "admin/unlinked-eans",
  ADMIN_CARD_INVENTORY: "admin/card-inventory",
  ME: "me",
  ME_PASSWORD: "me/password",
  SUPPORT: "support",
  ADMIN_SUPPORT: "admin/support",
});

const expireSession = () =>
  window.dispatchEvent(new Event(AUTH_SESSION_EXPIRED_EVENT));

const isSessionActive = (): boolean => {
  const session = localStorage.getItem(AuthEnum.SESSION);
  return !!session && new Date() < new Date(session);
};

// Cada petición autenticada alarga la sesión del front, de modo que solo
// caduca tras un periodo de inactividad (ver SESSION_DURATION_MS).
const touchSession = () => {
  const remember = localStorage.getItem(AuthEnum.REMEMBER) === "true";
  localStorage.setItem(AuthEnum.SESSION, getSessionExpiration(remember));
};

// Un único refresco en vuelo: si varias peticiones reciben 401 a la vez, todas
// esperan al mismo refresco en lugar de gastar el refresh token (de un solo uso)
// varias veces.
let refreshInFlight: Promise<boolean> | null = null;

const refreshAccessToken = (): Promise<boolean> => {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const refreshToken = localStorage.getItem(AuthEnum.REFRESH_TOKEN);
      if (!refreshToken) {
        return false;
      }
      try {
        const res = await fetch(API_URL.AUTH_REFRESH(), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (!res.ok) {
          return false;
        }
        const json = await res.json();
        if (!json?.token) {
          return false;
        }
        localStorage.setItem(AuthEnum.TOKEN, json.token);
        if (json.refresh_token) {
          localStorage.setItem(AuthEnum.REFRESH_TOKEN, json.refresh_token);
        }
        return true;
      } catch {
        return false;
      }
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

// fetch autenticado: añade el JWT y, si el API responde 401 (JWT caducado),
// lo renueva con el refresh token y repite la petición una vez. Si la sesión
// no se puede renovar, emite AUTH_SESSION_EXPIRED_EVENT.
export const apiFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  if (!isSessionActive()) {
    expireSession();
    return new Response(null, { status: 401 });
  }

  const sentToken = localStorage.getItem(AuthEnum.TOKEN);
  const doFetch = () =>
    fetch(url, {
      ...options,
      headers: {
        // Con FormData (subida de ficheros) el navegador pone el multipart con su separador.
        ...(options.body instanceof FormData
          ? {}
          : { "Content-Type": "application/json" }),
        Authorization: `Bearer ${localStorage.getItem(AuthEnum.TOKEN)}`,
        ...options.headers,
      },
    });

  let res = await doFetch();
  if (res.status === 401) {
    // Si otra petición ya renovó el JWT mientras esta estaba en vuelo, basta
    // con repetirla con el token nuevo, sin gastar otro refresco.
    const tokenAlreadyRenewed =
      localStorage.getItem(AuthEnum.TOKEN) !== sentToken;
    if (tokenAlreadyRenewed || (await refreshAccessToken())) {
      res = await doFetch();
    }
    if (res.status === 401) {
      expireSession();
      return res;
    }
  }

  touchSession();
  return res;
};

export interface ServiceResponseProps {
  error: boolean;
  messageError: string;
  data?: any;
}

// Descarga un fichero binario autenticado (p.ej. la exportación a Excel de
// Informes) como si fuese un enlace normal, ya que un <a href> no puede
// llevar la cabecera Authorization.
export const downloadFile = async (
  url: string,
  filename: string
): Promise<boolean> => {
  const res = await apiFetch(url);
  if (!res.ok) {
    return false;
  }

  const blob = await res.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);

  return true;
};
